import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table';
import type { ListSingleUseTokensResponse, SingleUseTokenListItem } from '@pillar-portal/api-types';

type TokenActionFn = (singleUseAuthTokenUuid: string) => Promise<{ message: string }>;
type TokenFetchFn = (outstandingOnly: boolean) => Promise<ListSingleUseTokensResponse>;

interface SingleUseTokensCardProps {
    title: string;
    queryKey: ReadonlyArray<unknown>;
    fetchTokens: TokenFetchFn;
    expireToken: TokenActionFn;
    resendToken: TokenActionFn;
    emptyOutstandingMessage: string;
    emptyHistoricalMessage: string;
    showUserColumns: boolean;
}

export const canResendSingleUseToken = (token: SingleUseTokenListItem): boolean => {
    return token.used_ts === null;
};

export function formatSingleUseTokenTimestamp(timestampIso: string | null): string {
    if (timestampIso === null) {
        return '-';
    }
    return new Date(timestampIso).toLocaleString();
}

interface SingleUseTokensTableProps {
    tokens: ReadonlyArray<SingleUseTokenListItem>;
    showUserColumns: boolean;
    isBusy: boolean;
    onExpire: (singleUseAuthTokenUuid: string) => void;
    onResend: (singleUseAuthTokenUuid: string) => void;
}

export function SingleUseTokensTable(props: SingleUseTokensTableProps) {
    return (
        <div className="rounded-md border">
            <Table>
                <TableHeader>
                    <TableRow>
                        <TableHead>Token UUID</TableHead>
                        <TableHead>Type</TableHead>
                        {props.showUserColumns && <TableHead>User</TableHead>}
                        <TableHead>Email</TableHead>
                        <TableHead>Expires</TableHead>
                        <TableHead>Used</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {props.tokens.map((token) => {
                        return (
                            <TableRow key={token.single_use_auth_token_uuid}>
                                <TableCell>{token.single_use_auth_token_uuid}</TableCell>
                                <TableCell>{token.type}</TableCell>
                                {props.showUserColumns && (
                                    <TableCell>
                                        {token.user_primary_email !== null
                                            ? token.user_primary_email
                                            : '-'}
                                    </TableCell>
                                )}
                                <TableCell>{token.email}</TableCell>
                                <TableCell>
                                    {formatSingleUseTokenTimestamp(token.expire_ts)}
                                </TableCell>
                                <TableCell>{formatSingleUseTokenTimestamp(token.used_ts)}</TableCell>
                                <TableCell>
                                    {token.is_outstanding
                                        ? 'Outstanding'
                                        : token.used_ts !== null
                                          ? 'Used'
                                          : token.is_expired
                                            ? 'Expired'
                                            : 'Unknown'}
                                </TableCell>
                                <TableCell className="text-right space-x-2">
                                    {token.is_outstanding && (
                                        <Button
                                            size="sm"
                                            variant="destructive"
                                            disabled={props.isBusy}
                                            onClick={() => props.onExpire(token.single_use_auth_token_uuid)}
                                        >
                                            Expire
                                        </Button>
                                    )}
                                    {canResendSingleUseToken(token) && (
                                        <Button
                                            size="sm"
                                            variant="default"
                                            disabled={props.isBusy}
                                            onClick={() => props.onResend(token.single_use_auth_token_uuid)}
                                        >
                                            Resend
                                        </Button>
                                    )}
                                </TableCell>
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
        </div>
    );
}

export function SingleUseTokensCard(props: SingleUseTokensCardProps) {
    const queryClient = useQueryClient();
    const [showAllHistoricalTokens, setShowAllHistoricalTokens] = useState(false);
    const [operationMessage, setOperationMessage] = useState<string | null>(null);
    const [operationErrorMessage, setOperationErrorMessage] = useState<string | null>(null);
    const outstandingOnly: boolean = !showAllHistoricalTokens;

    const tokensQuery = useQuery({
        queryKey: [...props.queryKey, { outstandingOnly }],
        queryFn: async () => {
            return await props.fetchTokens(outstandingOnly);
        },
    });

    const expireMutation = useMutation({
        mutationFn: async (singleUseAuthTokenUuid: string) => {
            return await props.expireToken(singleUseAuthTokenUuid);
        },
        onSuccess: async (response) => {
            setOperationErrorMessage(null);
            setOperationMessage(response.message);
            await queryClient.invalidateQueries({ queryKey: props.queryKey });
        },
        onError: (error: unknown) => {
            setOperationMessage(null);
            if (error instanceof Error) {
                setOperationErrorMessage(error.message);
            } else {
                setOperationErrorMessage('Failed to expire token');
            }
        },
    });

    const resendMutation = useMutation({
        mutationFn: async (singleUseAuthTokenUuid: string) => {
            return await props.resendToken(singleUseAuthTokenUuid);
        },
        onSuccess: async (response) => {
            setOperationErrorMessage(null);
            setOperationMessage(response.message);
            await queryClient.invalidateQueries({ queryKey: props.queryKey });
        },
        onError: (error: unknown) => {
            setOperationMessage(null);
            if (error instanceof Error) {
                setOperationErrorMessage(error.message);
            } else {
                setOperationErrorMessage('Failed to resend token');
            }
        },
    });

    const tokens: ReadonlyArray<SingleUseTokenListItem> = tokensQuery.data?.tokens ?? [];

    return (
        <Card>
            <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>{props.title}</CardTitle>
                <div className="flex items-center gap-2">
                    <Checkbox
                        id="showAllSingleUseTokens"
                        checked={showAllHistoricalTokens}
                        onCheckedChange={(checked) => setShowAllHistoricalTokens(checked === true)}
                    />
                    <Label htmlFor="showAllSingleUseTokens">Show all historical tokens</Label>
                </div>
            </CardHeader>
            <CardContent className="space-y-4">
                {operationMessage !== null && (
                    <Alert>
                        <AlertDescription>{operationMessage}</AlertDescription>
                    </Alert>
                )}
                {operationErrorMessage !== null && (
                    <Alert variant="destructive">
                        <AlertDescription>{operationErrorMessage}</AlertDescription>
                    </Alert>
                )}
                {tokensQuery.isLoading ? (
                    <p className="text-sm text-muted-foreground">Loading tokens...</p>
                ) : tokens.length === 0 ? (
                    <p className="text-sm text-muted-foreground">
                        {outstandingOnly
                            ? props.emptyOutstandingMessage
                            : props.emptyHistoricalMessage}
                    </p>
                ) : (
                    <SingleUseTokensTable
                        tokens={tokens}
                        showUserColumns={props.showUserColumns}
                        isBusy={expireMutation.isPending || resendMutation.isPending}
                        onExpire={(singleUseAuthTokenUuid) => {
                            expireMutation.mutate(singleUseAuthTokenUuid);
                        }}
                        onResend={(singleUseAuthTokenUuid) => {
                            resendMutation.mutate(singleUseAuthTokenUuid);
                        }}
                    />
                )}
            </CardContent>
        </Card>
    );
}
